import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import AdminSponsored from "./AdminSponsored";
import { adminVendorFlags, adminSetVendorFlags, adminVendorOverview, adminVendorReview, adminVendorUnclaim, adminPreviewEmail } from "../lib/api";

const FLAG_HELP = {
  claiming: "Free tier: 'claim this listing' on tool pages, /claim, /vendor, /for-vendors, submit-form hint, claim line in X posts",
  starter: "Paid tier: Starter upgrade buttons + Stripe checkout + owner page editor (needs claiming)",
  badges: "Rank badges (#3 Screener…, Top 10 · strategy) in the vendor dashboard",
  sponsored: "Sponsored slots: labelled rows on the homepage (max 2), /best/* and /alternatives/* lists (max 3), priced by tier; admin-assigned or bought by claimed vendors (monthly price, 3-month minimum)",
};

export default function AdminVendors() {
  const [flags, setFlags] = useState(null);
  const [ov, setOv] = useState(null);
  const load = useCallback(async () => {
    try {
      const [f, o] = await Promise.all([adminVendorFlags(), adminVendorOverview()]);
      setFlags(f);
      setOv(o);
    } catch {
      toast.error("could not load vendor data");
    }
  }, []);
  useEffect(() => {
    Promise.resolve().then(load);
  }, [load]);

  const toggle = async (k) => {
    try {
      setFlags(await adminSetVendorFlags({ [k]: !flags[k] }));
      toast.success(`${k} ${!flags[k] ? "ON" : "off"}`);
    } catch {
      toast.error("could not update");
    }
  };
  const review = async (slug, action) => {
    const note = action === "reject" ? window.prompt("reason for the owner (optional)") || null : null;
    try {
      await adminVendorReview(slug, action, note);
      toast.success(`${action}d`);
      load();
    } catch {
      toast.error("failed");
    }
  };
  const unclaim = async (slug) => {
    if (!window.confirm(`remove the owner from ${slug}?`)) return;
    await adminVendorUnclaim(slug);
    load();
  };
  const [previewTo, setPreviewTo] = useState("");
  const [previewing, setPreviewing] = useState("");
  const previewEmail = async (type) => {
    setPreviewing(type);
    try {
      const r = await adminPreviewEmail(type, previewTo || undefined);
      toast.success(`sent to ${r.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not send preview");
    } finally {
      setPreviewing("");
    }
  };

  if (!flags || !ov) return <div className="text-[13px] text-[#888]">loading…</div>;
  return (
    <div className="space-y-8 text-[12px]" data-testid="admin-vendors">
      <section data-testid="vendor-flags">
        <h2 className="text-[15px] font-bold border-b border-black pb-1">launch switches</h2>
        <p className="text-[#444] mt-2">Everything below is built and deployed but hidden until switched on. Takes effect instantly, no redeploy. You (admin) always see it on — use that to preview on the live site.</p>
        <ul className="mt-3 space-y-2">
          {Object.keys(FLAG_HELP).map((k) => (
            <li key={k} className="flex items-baseline gap-3">
              <button type="button" className={`tv-btn ${flags[k] ? "tv-btn-primary" : ""}`} onClick={() => toggle(k)} data-testid={`vendor-flag-${k}`}>
                [{flags[k] ? "ON " : "off"}] {k}
              </button>
              <span className="text-[#666]">{FLAG_HELP[k]}</span>
            </li>
          ))}
        </ul>
        <p className="text-[#666] mt-2">preview: <a href="/for-vendors" className="tv-link" target="_blank" rel="noreferrer">/for-vendors</a> · <a href="/vendor" className="tv-link" target="_blank" rel="noreferrer">/vendor</a></p>
      </section>

      <section data-testid="vendor-email-previews">
        <h2 className="text-[15px] font-bold border-b border-black pb-1">preview vendor emails</h2>
        <p className="text-[#444] mt-2">Send yourself a live copy of each automated vendor email so you can see exactly what owners get. Uses your top listing as the sample.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            type="email"
            value={previewTo}
            onChange={(e) => setPreviewTo(e.target.value)}
            placeholder="send to (defaults to your reply-to)"
            className="tv-input text-[12px] w-64"
            data-testid="preview-email-to"
          />
          <button type="button" className="tv-btn" disabled={!!previewing} onClick={() => previewEmail("starter_welcome")} data-testid="preview-starter-welcome">
            {previewing === "starter_welcome" ? "…" : "[Starter welcome]"}
          </button>
          <button type="button" className="tv-btn" disabled={!!previewing} onClick={() => previewEmail("starter_ended")} data-testid="preview-starter-ended">
            {previewing === "starter_ended" ? "…" : "[Starter ended]"}
          </button>
          <button type="button" className="tv-btn" disabled={!!previewing} onClick={() => previewEmail("sponsor_welcome")} data-testid="preview-sponsor-welcome">
            {previewing === "sponsor_welcome" ? "…" : "[Sponsor welcome]"}
          </button>
        </div>
      </section>

      <AdminSponsored />

      <section data-testid="vendor-pending-profiles">
        <h2 className="text-[15px] font-bold border-b border-black pb-1">page edits waiting for review ({ov.pending_profiles.length})</h2>
        {ov.pending_profiles.length === 0 ? <p className="text-[#888] mt-2">none.</p> : (
          <ul className="mt-2 space-y-3">
            {ov.pending_profiles.map((t) => (
              <li key={t.slug} className="border border-black p-3" data-testid={`vendor-review-${t.slug}`}>
                <div className="font-bold">
                  {t.title} <span className="text-[#666] font-normal">· {t.claimed_by}</span>
                  {" "}<a href={`/tool/${t.slug}`} className="tv-link text-[12px]" target="_blank" rel="noreferrer">(view live page ↗)</a>
                </div>
                <ProfileReview draft={t.vendor} live={t.vendor_live} />
                <div className="mt-2 flex gap-2">
                  <button type="button" className="tv-btn tv-btn-primary" onClick={() => review(t.slug, "approve")} data-testid={`vendor-approve-${t.slug}`}>[approve &amp; publish]</button>
                  <button type="button" className="tv-btn" onClick={() => review(t.slug, "reject")} data-testid={`vendor-reject-${t.slug}`}>[reject]</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section data-testid="vendor-claimed">
        <h2 className="text-[15px] font-bold border-b border-black pb-1">
          claimed listings ({ov.claimed.length}) · starter active: {ov.starter_active} · pending claims: {ov.pending_claims.length}
        </h2>
        <ul className="mt-2 space-y-1">
          {ov.claimed.map((t) => (
            <li key={t.slug} className="flex flex-wrap gap-x-3" data-testid={`vendor-claimed-${t.slug}`}>
              <a href={`/tool/${t.slug}`} className="tv-link" target="_blank" rel="noreferrer">{t.slug}</a>
              <span className="text-[#666]">{t.claimed_by} · {String(t.claimed_at || "").slice(0, 10)} · {t.plan === "starter" && t.plan_status === "active" ? `★ starter (${t.plan_interval})` : "free"}</span>
              <button type="button" className="tv-link underline text-[#b91c1c]" onClick={() => unclaim(t.slug)} data-testid={`vendor-unclaim-${t.slug}`}>unclaim</button>
            </li>
          ))}
        </ul>
        {ov.pending_claims.length > 0 && (
          <details className="mt-2"><summary className="cursor-pointer">pending (meta-tag) claims</summary>
            <ul className="mt-1 pl-4 list-disc text-[#444]">{ov.pending_claims.map((c) => <li key={c.id}>{c.slug} — {c.email} — {String(c.created_at).slice(0, 10)}</li>)}</ul>
          </details>
        )}
      </section>
    </div>
  );
}

// Human-readable review of a pending vendor profile edit, highlighting what changed
// vs the currently-live listing. Replaces the old raw-JSON dump.
function ProfileReview({ draft, live }) {
  const d = draft || {};
  const l = live || {};
  const changed = (a, b) => JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);
  const Row = ({ label, isChanged, children }) => (
    <div className="py-2 border-t border-[#eee] first:border-t-0">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-[#666]">{label}</span>
        {isChanged && <span className="text-[10px] font-bold bg-[#ffff00] px-1.5 py-0.5">changed</span>}
      </div>
      <div className="mt-1 text-[13px] text-[#111]">{children}</div>
    </div>
  );
  const normShots = (arr) => (Array.isArray(arr) ? arr : []).map((s) => (typeof s === "string" ? { url: s, caption: "" } : s)).filter((s) => s?.url);
  const dShots = normShots(d.screenshots);
  const dCtas = Array.isArray(d.ctas) ? d.ctas.filter((c) => c?.url || c?.label) : [];
  const img = (u) => (u || "").startsWith("/") || /^https?:\/\//.test(u || "");

  return (
    <div className="mt-2 bg-[#fafafa] border border-[#ddd] px-3">
      {d.positioning != null && d.positioning !== "" && (
        <Row label="Pitch" isChanged={changed(d.positioning, l.positioning)}>
          <p className="whitespace-pre-wrap leading-snug m-0">{d.positioning}</p>
        </Row>
      )}
      {(d.cta_label || d.cta_url) && (
        <Row label="Primary button" isChanged={changed([d.cta_label, d.cta_url], [l.cta_label, l.cta_url])}>
          <span className="font-bold">{d.cta_label || "(no label)"}</span>
          {d.cta_url && <span className="text-[#666]"> → {d.cta_url}</span>}
        </Row>
      )}
      {dCtas.length > 0 && (
        <Row label="Buttons" isChanged={changed(d.ctas, l.ctas)}>
          <ul className="list-disc pl-5 m-0">
            {dCtas.map((c, i) => (
              <li key={i}><span className="font-bold">{c.label || "(no label)"}</span>{c.url ? <span className="text-[#666]"> → {c.url}</span> : null}</li>
            ))}
          </ul>
        </Row>
      )}
      {d.logo && (
        <Row label="Logo" isChanged={changed(d.logo, l.logo)}>
          {img(d.logo) ? <img src={d.logo} alt="logo" className="h-12 border border-[#ccc] bg-white" /> : <span>{d.logo}</span>}
        </Row>
      )}
      {d.video_url && (
        <Row label="Video" isChanged={changed(d.video_url, l.video_url)}>
          <a href={d.video_url} className="tv-link" target="_blank" rel="noreferrer">{d.video_url}</a>
        </Row>
      )}
      {dShots.length > 0 && (
        <Row label={`Screenshots (${dShots.length})`} isChanged={changed(d.screenshots, l.screenshots)}>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {dShots.map((s, i) => (
              <div key={i} className="border border-[#ccc]">
                <img src={s.url} alt="" className="w-full h-14 object-cover block bg-white" />
                {s.caption && <div className="text-[10px] text-[#444] px-1 py-0.5 leading-tight">{s.caption}</div>}
              </div>
            ))}
          </div>
        </Row>
      )}
      {Array.isArray(d.integrations) && d.integrations.length > 0 && (
        <Row label="Integrations" isChanged={changed(d.integrations, l.integrations)}>
          {d.integrations.join(", ")}
        </Row>
      )}
      {!d.positioning && !d.cta_label && !d.cta_url && dCtas.length === 0 && !d.logo && !d.video_url && dShots.length === 0 && (
        <p className="py-2 text-[#888]">No content fields set in this edit.</p>
      )}
    </div>
  );
}

